import React, { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePostHog } from 'posthog-js/react';

import photo from '../../assets/images/profile-photo-base.png';
import { LANDING } from '../../configs/api/api.config';
import { useCameraPreview } from '../../hooks/useCameraPreview';
import { IConsultation } from '../../store/types';
import { getDate, getTime } from '../../utils/getTime';
import Button from '../common/button';
import Header from '../header';
import { VideoIcon } from '../icons/video';
import { Settings } from '../icons/settings';
import { useVpnDetection } from '../../hooks/useVpnDetection';
import { useLobbyBackgroundBlur } from '../../hooks/useLobbyBackgroundBlur';

import { LobbyBlurToggle } from './lobby-blur-toggle';
import { VpnWarningBanner } from './vpn-warning-banner';

type ClientJoinProps = {
  consultation?: IConsultation;
  setStarted: (val: boolean, isCameraEnabled?: boolean) => void | Promise<void>;
  setOpenSettings: (val: boolean) => void | Promise<void>;
  users: any[];
  disabled?: boolean;
  isRoomInitialized?: boolean | null;
  /** When true, multiple clients can join (group therapy); skip "one client only" check */
  isGroupConsultation?: boolean;
  /** Групповая терапия: название группы (full_name клиента-группы) для отображения */
  groupName?: string;
  /** Групповая терапия: по клику «Запросить разрешение» отправляется запрос психологу; передаётся имя участника и состояние камеры */
  onRequestPermission?: (displayName: string, isCameraEnabled: boolean) => void;
};

export const ClientJoin: FC<ClientJoinProps> = ({
  consultation,
  setStarted,
  setOpenSettings,
  users,
  disabled,
  isRoomInitialized,
  isGroupConsultation = false,
  groupName,
  onRequestPermission,
}) => {
  const [isJoining, setIsJoining] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [vpnDismissed, setVpnDismissed] = useState(false);
  const { isVpn, isChecking } = useVpnDetection();
  const posthog = usePostHog();
  const vpnEventSentRef = useRef(false);

  useEffect(() => {
    if (isChecking || vpnEventSentRef.current || !posthog) return;
    vpnEventSentRef.current = true;
    posthog.capture('meet_join_vpn_status', { is_vpn: isVpn, role: 'client' });
  }, [isChecking, isVpn, posthog]);

  const { videoRef, stream, isLoading, cameraError, isCameraEnabled, toggleCamera, stopStream } = useCameraPreview();
  const blur = useLobbyBackgroundBlur(stream ?? null, videoRef);

  const handleJoin = async () => {
    if (!isGroupConsultation) {
      const hasClient = users.find((el) => el.display?.split('-').at(-1)?.trim() === 'client');
      if (hasClient) {
        toast.error('Каждый участник может подключить только одно устройство');

        return;
      }
    }

    if (isGroupConsultation && onRequestPermission) {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim();
      if (!name) {
        toast.error('Введите имя и фамилию');
        return;
      }
      stopStream();
      onRequestPermission(name, isCameraEnabled);
      return;
    }

    stopStream();
    setIsJoining(true);
    await setStarted(true, isCameraEnabled);
  };

  const clientAlreadyIn = users.some((el) => el.display?.split('-').at(-1)?.trim() === 'client');
  const isOnlyPsychologist = users.length === 1 && users[0]?.display?.split('-').at(-1)?.trim() === 'psychologist';
  /** Групповая терапия: психолог уже на встрече (иначе кнопку дизейблим и показываем «Ожидаем психолога…») */
  const psychologistInRoom = users.some((el) => el.display?.split('-').at(-1)?.trim() === 'psychologist');

  return (
    <>
      <div className="h-full w-full flex flex-col bg-red">
        <Header />
        <div className="w-full shrink-0 h-[50px] lg:h-[80px]" />
        {isVpn && !vpnDismissed && <VpnWarningBanner onDismiss={() => setVpnDismissed(true)} />}
        {consultation && (
          <div className="w-full flex flex-col overflow-y-auto">
            <div className="w-full flex flex-col items-center py-[30px]">
              <h2 className="flex flex-col font-comfortaa font-bold text-blackBlue text-xl lg:text-4xl mb-[4vh] text-center">
                <span>ZiNGiZi</span>
                <span>{isGroupConsultation ? 'Групповая встреча' : 'Консультация'}</span>
              </h2>
              {isGroupConsultation && (
                <div className="text-greyLightBlue text-sm mb-4 -mt-[2vh] text-center">до 20 участников</div>
              )}
              <div className="flex gap-[22px] lg:gap-[45px] mb-[4vh]">
                {!isGroupConsultation && (
                  <div className="w-[140px]">
                    <div className="w-[140px] h-[140px] overflow-hidden">
                      <img
                        className="w-full h-full object-contain"
                        src={
                          consultation.psychologist.avatar
                            ? `${import.meta.env.VITE_REACT_APP_API_URL}${consultation.psychologist.avatar}`
                            : photo
                        }
                        alt="Психолог"
                      />
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-[8px]">
                  {isOnlyPsychologist && (
                    <div className="text-blackBase text-center p-[5px_20px] rounded-[10px] bg-greenLight mb-[7px]">
                      Психолог ожидает
                    </div>
                  )}

                  <div className="flex gap-[15px]">
                    <span className="text-greyLightBlue">Дата:</span>
                    <span className="text-blackBase">{getDate(new Date(consultation?.date))}</span>
                  </div>
                  <div className="flex gap-[15px]">
                    <span className="text-greyLightBlue">Время:</span>
                    <span className="text-blackBase">{`${
                      consultation?.datetime_start && getTime(new Date(consultation?.datetime_start))
                    } - ${consultation?.datetime_end && getTime(new Date(consultation?.datetime_end))}`}</span>
                  </div>
                  <div className="flex gap-[15px]">
                    <span className="text-greyLightBlue">Психолог:</span>
                    <span className="text-blackBase">{`${consultation.psychologist.first_name}`}</span>
                  </div>
                  {!isGroupConsultation && (
                    <div className="flex gap-[15px]">
                      <span className="text-greyLightBlue">Сессия:</span>
                      <span className="text-blackBase">{`№${consultation.consultation_number}`}</span>
                    </div>
                  )}

                  {isGroupConsultation && (
                    <div className="flex gap-[15px] mt-1">
                      <span className="text-greyLightBlue">Название группы:</span>
                      <span className="text-blackBase">{groupName || '—'}</span>
                    </div>
                  )}
                </div>
              </div>
              {isGroupConsultation && (
                <div className="flex items-center gap-3 mb-6 w-full max-w-[344px] sm:max-w-[390px]">
                  <label className="text-greyLightBlue text-sm shrink-0">Имя</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Введите имя"
                    className="w-full px-4 py-3 rounded-[10px] border border-greyLightBlue text-blackBase placeholder:text-greyBase focus:outline-none focus:border-blackBase"
                  />
                </div>
              )}
              <div className="w-full max-w-[344px] sm:max-w-[390px] mb-[4vh] rounded-2xl overflow-hidden bg-gray-900 relative aspect-video shadow-md">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover -scale-x-100"
                />

                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <span className="animate-spin w-7 h-7 border-2 border-white border-t-transparent rounded-full" />
                  </div>
                )}

                {cameraError && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white text-center px-4 gap-2">
                    <VideoIcon checked width={40} height={40} />
                    <p className="text-xs leading-relaxed">{cameraError}</p>
                  </div>
                )}

                {!isCameraEnabled && !cameraError && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white gap-2">
                    <VideoIcon checked width={40} height={40} />
                    <p className="text-xs">Камера выключена</p>
                  </div>
                )}

                <div className="absolute bottom-2 left-2 flex gap-2">
                  <button
                    onClick={toggleCamera}
                    disabled={!!cameraError}
                    className="disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <VideoIcon checked={!isCameraEnabled} width={36} height={36} />
                  </button>
                  <LobbyBlurToggle
                    isBlurEnabled={blur.isBlurEnabled}
                    toggleBlur={blur.toggleBlur}
                    isSupported={blur.isSupported}
                    isPreparing={blur.isPreparing}
                    disabled={!!cameraError || !isCameraEnabled}
                  />
                </div>
              </div>

              {clientAlreadyIn ? (
                <div className="text-white p-[10px_20px] rounded-[10px] bg-redBase mb-[15px]">
                  <span>У Вас уже есть активная сессия</span>
                </div>
              ) : (
                <Button
                  className="!w-[344px] sm:!w-[390px] mb-[10px] p-[15px_20px] md:p-[15px_60px] max-w-[90%]"
                  onClick={() => handleJoin()}
                  disabled={
                    disabled ||
                    isJoining ||
                    (isGroupConsultation && !`${firstName.trim()} ${lastName.trim()}`.trim()) ||
                    (isGroupConsultation && !psychologistInRoom)
                  }
                  loading={isJoining || isRoomInitialized === null}
                >
                  {isJoining
                    ? 'Подключение'
                    : isRoomInitialized === null
                      ? 'Инициализация'
                      : isGroupConsultation && !psychologistInRoom
                        ? 'Ожидаем психолога…'
                        : isGroupConsultation
                          ? 'Запросить разрешение'
                          : 'Подключиться к сессии'}
                </Button>
              )}

              <small className="text-[#70707c] text-[9px] text-center max-w-[344px] sm:max-w-[390px] mb-[4vh]">
                Нажимая кнопку «Подключиться к сессии», я принимаю условия{' '}
                <Link
                  className="underline"
                  to={`${LANDING}/wp-content/uploads/2025/03/polzovatelskoe_soglashenie_logosterapi_rev.pdf`}
                  target="_blank"
                >
                  Пользовательского соглашения
                </Link>
                , даю
                <Link
                  className="underline"
                  to={`${LANDING}/wp-content/uploads/2025/03/politika_konfidenczialnosti_logosterapi_rev.pdf`}
                  target="_blank"
                >
                  согласие на обработку персональных данных
                </Link>
                и подтверждаю предоставление Психологу своего согласия на обработку моих персональных данных.
              </small>
              <p className="text-center text-greyBase text-sm lg:text-base mb-[4vh] lg:mb-[30px]  max-w-[344px]">
                Проверьте настройки видео-конференции перед её началом.
              </p>
              <Button
                className="!w-[344px] sm:!w-[390px] p-[15px_30px] flex items-center relative max-w-[90%]"
                type="grey"
                disabled={isJoining}
                onClick={async () => {
                  await setOpenSettings(true);
                }}
              >
                <span className="grow">Настройки</span>

                <Settings
                  needHover={false}
                  fill="none"
                  className="absolute top-[50%] right-[20px] -translate-y-1/2 z-1"
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
